/**
 * The signed-in person's own staff-notification choices (PM 465c33cc).
 *
 * Which ROLES get a notice is the club's setting (Settings > Manage
 * Notifications). Here a person turns a notice their role gets off, or back
 * on, for themselves only. The server writes only the caller's own row; there
 * is no way to name anyone else.
 */
import axios from "./axiosInstance";
import config from "./config";
import { getTenantId, getAuthSource } from "./apiHelpers";

export interface MyNotification {
  notification_key: string;
  /** The club sends this notice to the person's role. */
  sent_to_role: boolean;
  /** The person turned it off for themselves. */
  opted_out: boolean;
  /** What actually happens: sent to the role and not turned off. */
  enabled: boolean;
  /** How urgent the notice is (PM 0061277c). */
  level?: NoticeLevel;
}

export type NoticeLevel = "urgent" | "normal" | "low";
export type NoticeChannel = "bell" | "email" | "push";
/** Per level and channel: true / false, or null for "as each notice is sent today". */
export type NoticeDelivery = Record<NoticeLevel, Record<NoticeChannel, boolean | null>>;

export interface MyNotificationsResult {
  success: boolean;
  notifications?: MyNotification[];
  /** The person's channel choices per level. */
  delivery?: NoticeDelivery;
  /** The club's daily digest (Settings > Manage Notifications). */
  digest?: { enabled: boolean; hour: number };
  /** The club has push turned on. */
  pushAvailable?: boolean;
  error?: string;
  /** 404: the person has no staff profile in this club. */
  noProfile?: boolean;
}

const endpoint = () => `${config.BASE_URL}/users/me/notification-preferences`;

const headers = () => ({
  "Content-Type": "application/json",
  "x-tenant-id": getTenantId(),
  "x-auth-source": getAuthSource(),
  authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

const fromResponse = (res: any, fallback: string): MyNotificationsResult => {
  const data = res?.data?.data;
  const list = data?.notifications;
  if (res?.data?.success && Array.isArray(list)) {
    return {
      success: true,
      notifications: list,
      delivery: data?.delivery,
      digest: data?.digest,
      pushAvailable: data?.push_available === true,
    };
  }
  return { success: false, error: res?.data?.error || fallback };
};

const fromError = (err: any, fallback: string): MyNotificationsResult => ({
  success: false,
  error: err?.response?.data?.error || fallback,
  noProfile: err?.response?.status === 404,
});

export async function getMyNotifications(): Promise<MyNotificationsResult> {
  const fallback = "Could not load your notification settings";
  try {
    return fromResponse(await axios.get(endpoint(), { headers: headers() }), fallback);
  } catch (err) {
    return fromError(err, fallback);
  }
}

export async function setMyNotification(notificationKey: string, enabled: boolean): Promise<MyNotificationsResult> {
  const fallback = "Could not save your notification setting";
  try {
    return fromResponse(
      await axios.put(endpoint(), { notification_key: notificationKey, enabled }, { headers: headers() }),
      fallback,
    );
  } catch (err) {
    return fromError(err, fallback);
  }
}

/** Choose how one level reaches the person on one channel; null goes back to "as usual". */
export async function setMyNoticeDelivery(
  level: NoticeLevel,
  channel: NoticeChannel,
  value: boolean | null,
): Promise<MyNotificationsResult> {
  const fallback = "Could not save how you get notices";
  try {
    return fromResponse(await axios.put(endpoint(), { level, channel, value }, { headers: headers() }), fallback);
  } catch (err) {
    return fromError(err, fallback);
  }
}
